import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAuth } from 'hooks/useAuth';
import { getAuthToken } from 'utils/authTyped';
import { createRemoteComponent } from 'mfe/runtime';

import { inventoryRemote } from './config';
import { InventoryLoadingSkeleton } from './InventoryLoadingSkeleton';
import { InventoryRemoteError } from './InventoryRemoteError';

/**
 * Props contract exposed by the inventory microfrontend (`inventory/App`).
 * Source of truth: `parrot-inventory-web/src/App.tsx`. To be replaced by the
 * types-only package `@parrot-software/mfe-contracts` (RFC-PLATFORM-003).
 */
type InventoryAppProps = {
  basename?: string;
  getAuthToken?: () => string | undefined;
  hasModuleAccess?: boolean;
  locale?: string;
  navigateHost?: (path: string) => void;
  setDocumentTitle?: (title: string) => void;
  /** The store selected in the shell. The remote preselects the supply centre that belongs to it. */
  store?: { name?: string; uuid?: string } | null;
  user?: { email?: string } | null;
};

const InventoryApp = createRemoteComponent<InventoryAppProps>({
  fallback: InventoryRemoteError,
  loading: InventoryLoadingSkeleton,
  module: 'App',
  remote: inventoryRemote,
});

/**
 * Mount point for the inventory microfrontend inside the shell layout.
 * Wires the host contract: basename, session, locale, cross-app navigation and the tab title.
 */
type RemoteInventoryAppProps = {
  /**
   * Si el host resolvió el acceso al módulo. El remote **falla cerrado** con
   * `undefined` a propósito, así que no basta con montarlo: hay que decírselo.
   */
  hasModuleAccess: boolean;
};

/**
 * El token con el que el microfrontend habla con **su** backend: la sesión del shell.
 *
 * Antes esto leía `REACT_APP_INVENTORY_TOKEN`, porque `parrot-inventory-api-v2` firmaba con otro
 * secreto y el `accessToken` de aquí no valía allá. **Ya no**: el despliegue de V2 en staging usa
 * el mismo `jwt_secret` que inventory-api, que es el que valida los tokens que emite `parrot-api`.
 * Comprobado contra `inventory-api-staging`: con este token la API responde 200; sin él, 401
 * `auth.InvalidToken`.
 *
 * Se lee en CADA petición y no una vez: la sesión se renueva, y un valor capturado al montar
 * seguiría mandando el viejo — un 401 que sólo aparece «al rato».
 *
 * `REACT_APP_INVENTORY_TOKEN` se conserva como override para apuntar a un backend que firme con
 * otro secreto (un api-v2 local, por ejemplo) sin tocar código.
 */
const inventoryToken = (): string | undefined => process.env.REACT_APP_INVENTORY_TOKEN ?? getAuthToken()?.accessToken;

export const RemoteInventoryApp = ({ hasModuleAccess }: RemoteInventoryAppProps) => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const { store, user } = useAuth();
  // The remote only names the screen; Helmet stays the single writer of the tab and applies the shell's template.
  const [documentTitle, setDocumentTitle] = useState<string>();

  return (
    <>
      {documentTitle && (
        <Helmet>
          <title>{documentTitle}</title>
        </Helmet>
      )}
      <InventoryApp
        basename="/inventories"
        getAuthToken={inventoryToken}
        hasModuleAccess={hasModuleAccess}
        locale={i18n.language}
        navigateHost={(path) => history.push(path)}
        setDocumentTitle={setDocumentTitle}
        store={store ? { name: store.name, uuid: store.uuid } : null}
        user={user ? { email: user.email } : null}
      />
    </>
  );
};
